import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./buttons/BaseButton";
import { BUTTON_DISABLED_CLASSES } from "./buttons/helpers";

import type { PolymorphicRef } from "types";
import { Link } from "utils/routing";

export const CHIP_KINDS = [
  "blue",
  "hibiscus",
  "hibiscusBorderless",
  "moss",
  "mossBorderless",
  "orange",
  "red",
  "white",
  "gray", // TODO: consider removing as it's only used in one place, is identical to the disabled chip button state, and is not officially part of the MDS
  "outcome",
] as const;

export type ChipKind = (typeof CHIP_KINDS)[number];

export const CHIP_BUTTON_KINDS = [
  "blue",
  "hibiscus",
  "moss",
  "orange",
  "red",
  "white",
  "outcome",
] as const satisfies readonly ChipKind[];

const COMMON_CLASSES =
  "flex w-fit items-center justify-center gap-1.5 whitespace-nowrap rounded-3xl border px-3 py-1 h-[30px] text-xs";

const KIND_BASE_CLASSES: Record<ChipKind, string> = {
  blue: "text-blue bg-blue-tint-95 border-blue-tint-70",
  hibiscus: "text-hibiscus bg-hibiscus-tint-95 border-hibiscus-tint-70",
  hibiscusBorderless: "text-hibiscus bg-hibiscus-tint-95 border-hibiscus-tint-95",
  moss: "text-moss bg-moss-tint-95 border-moss-tint-70",
  mossBorderless: "text-moss bg-moss-tint-93 border-moss-tint-93",
  orange: "text-orange-shade-40 bg-orange-tint-90 border-orange-tint-70",
  red: "text-red-shade-20 bg-red-tint-93 border-red-tint-80",
  white: "text-black-tint-20 bg-black-tint-99 border-black-tint-90",
  gray: "text-black-tint-55 bg-black-tint-97 border-black-tint-90",
  outcome: "text-sm text-blue-shade-20 bg-blue-tint-95 border-blue-tint-70 ",
};

const KIND_BUTTON_CLASSES: Record<ChipKind, string> = {
  blue: "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--blue-tint-95)]",
  hibiscus:
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--hibiscus-tint-95)]",
  hibiscusBorderless: "",
  moss: "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--moss-tint-95)]",
  mossBorderless: "",
  orange:
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--orange-tint-90)]",
  red: "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--red-tint-93)]",
  white: "hover:bg-black-tint-97",
  gray: "",
  outcome:
    "hover:bg-blue-tint-90 " +
    "data-[selected]:text-white data-[selected]:bg-blue-shade-20 data-[selected]:border-blue-shade-20 " +
    "data-[selected]:hover:bg-blue",
};

function getChipClassName(kind: ChipKind, isButton: boolean, className?: string) {
  return twMerge(
    COMMON_CLASSES,
    KIND_BASE_CLASSES[kind],
    isButton && twMerge(BUTTON_DISABLED_CLASSES, KIND_BUTTON_CLASSES[kind], "cursor-pointer"),
    className,
  );
}

export type ChipProps<T extends React.ElementType = "div"> = BaseButtonProps<T> & {
  kind: ChipKind;
};

/* Renders as a div by default, but can be rendered as a button by passing the `as` prop:
 * <Chip as="button" kind="blue">I'm a button!</Chip>
 * Can also be rendered as a Link:
 * <Chip as={Link} to="/somewhere" kind="blue">I'm a link!</Chip>
 */
export const Chip = forwardRef<HTMLDivElement, Omit<ChipProps<"div">, "ref">>(function Chip(
  { as = "div", kind, className, children, ...rest },
  ref,
) {
  const includeHoverStyles = ["button", Link, "a"].includes(as);
  return (
    <BaseButton
      as={as}
      className={getChipClassName(kind, includeHoverStyles, className)}
      ref={ref}
      {...rest}
    >
      {children}
    </BaseButton>
  );
}) as <T extends React.ElementType = "div">(
  props: ChipProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
